import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import { MapArea } from '../../../interfaces/services/geography';
import { FeatureTypes, MapSources } from '../enums';

/** Interface used for the properties object on the Areas Map Source. */
export interface AreasMapSourceProperties {
    id: string;
    featureType: FeatureTypes;
    color: string;
    outlineColor?: string;
    description: string;
}

export const determineAreasSource = ({
    areas,
    areaNames,
    theme,
}: {
    areas: MapArea[];
    areaNames: string[];
    theme: Theme;
}): GeoJSON.Feature<GeoJSON.Geometry, AreasMapSourceProperties>[] => {
    const features = areas.map((area, index): GeoJSON.Feature<GeoJSON.Geometry, AreasMapSourceProperties> => {
        const pickupAreaTooltipHtml = `
            <div class='mapTooltip'>
                <div class='mapTooltipRow'>
                    <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                    <div class='mapTooltipCell'><strong>${areaNames[index]}</strong></div>
                </div>
                <div class='mapTooltipRow'>
                    <div class='mapTooltipCell'>Area Id:</div>
                    <div class='mapTooltipCell'>${area.id}</div>
                </div>
            </div>
        `;

        return {
            ...area.geography,
            properties: {
                id: area.id,
                featureType: FeatureTypes.AreaPolygon,
                color: theme.palette.info.main,
                outlineColor: theme.palette.primary.main,
                description: pickupAreaTooltipHtml,
            },
        };
    });

    return features;
};

/**
 * Loop through the areas and add a geometry feature to the source for each area location.
 * Adds the areas source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param areas The areas to show on the map
 * @param areaName The areaName to use in the area description
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addAreasSource = ({
    map,
    areas,
    areaName,
    theme,
}: {
    map: TrimbleMaps.Map;
    areas: MapArea[];
    areaName: string;
    theme: Theme;
}): GeoJSONSourceRaw => {
    const features = determineAreasSource({
        areas,
        areaNames: [areaName],
        theme,
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features,
        },
        generateId: true,
    };

    if (map.getSource(MapSources.areasSource) === undefined) {
        map.addSource(MapSources.areasSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.areasSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
